import "./MetricsTable.scss";
import * as Dfns from "date-fns/fp";
import { evaluate } from "mathjs";
import * as R from "ramda";
import { useCallback, useMemo } from "react";
import * as uuid from "uuid";
import {
  ColumnOrMetricName,
  DimensionColNames,
  DimensionColPrettyNames,
} from "@blisspointmedia/bpm-types/dist/Columns";
import * as CC from "@blisspointmedia/bpm-types/dist/CrossChannelPerformance";
import { FilterPaneState } from "@blisspointmedia/bpm-types/dist/FilterPane";
import {
  resolveDecimals,
  toPrettyPercent as toPrettyPercentNew,
} from "@blisspointmedia/bpm-types/dist/formatUtils";
import { KpiInfo } from "@blisspointmedia/bpm-types/dist/Kpis";
import * as L from "@blisspointmedia/bpm-types/dist/LinearPerformance";
import {
  CPX_FORMATTER,
  FormatterNew,
  IMP_IN_THOUSANDS_FORMATTER,
  SPEND_OR_REVENUE_FORMATTER,
  toPrettyDeltaPercent,
  toPrettyNumber as toPrettyNumberNew,
} from "@blisspointmedia/bpm-types/dist/metricFormatUtils";
import {
  Column,
  DimensionColumn,
  HeatMapData,
  MetricsTablePreset,
  PerformanceData,
  SuperHeader,
} from "@blisspointmedia/bpm-types/dist/MetricsTable";
import { PerformanceDataRow, SortInfo } from "@blisspointmedia/bpm-types/dist/Performance";
import { CustomMetric } from "@blisspointmedia/bpm-types/dist/pgTables/CustomMetric";
import { DATE_FORMAT } from "@blisspointmedia/bpm-types/dist/RelativeDatePicker";
import { COMMERCE } from "@blisspointmedia/bpm-types/dist/ScpChannels";
import { GroupedSegment } from "@blisspointmedia/bpm-types/dist/SegmentationMapping";
import * as S from "@blisspointmedia/bpm-types/dist/StreamingPerformance";
import { Placement } from "../../Components/OverlayTrigger";
import {
  DELIVERY_CURRENCY_CODE,
  PERFORMANCE_CURRENCY_CODE,
} from "../../CrossChannel/crossChannelConstants";
import {
  DivergentColorMap20,
  DivergentGreenDark,
  getSteppedHeatColor,
  SequentialGreenRamp,
} from "../../utils/colors";
import { mergeNumberObjects } from "../../utils/data";
import { StateSetter } from "../../utils/types";
import { GLOBAL_KPI } from "../MetricsTableConfig/CustomMetricsModal";

// Both ramps are authored min -> max for a column where a HIGHER value is the better one. A
// min-is-best column reads them backwards. Hoisted rather than reversed inline because the heat
// style is computed once per cell.
const DIVERGENT_STOPS_MIN_IS_BEST = [...DivergentColorMap20].reverse();
const SEQUENTIAL_STOPS_MIN_IS_BEST = [...SequentialGreenRamp].reverse();

export interface CellData {
  backgroundColor?: string;
  content?: JSX.Element; // Pass in custom elements
  italic?: boolean;
  label: string;
  overlayText?: string;
  url?: string;
  value: number | string;
  adInfo?: AdInfoRow;
}

export interface SuperHeaderItem {
  leftDivider: boolean;
  rightDivider: boolean;
  text: string;
}

export interface AdInfoRow {
  company: string;
  platform: string;
  ad_name: string;
  ad_id: string;
  account_id: string;
}

export const CDN = "https://cdn.blisspointmedia.com";

export const getCreativeAsset = (company: string, file: string): string =>
  `${CDN}/creativeAssets/assets/${company}/${file}`;
export const getCreativeThumbnail = (company: string, isci: string): string =>
  `${CDN}/creativeAssets/thumbnails/${company}/${isci}.png`;

export const TEXT_SIZE_MAP = {
  Metrics: "regular",
  SparkChart: "extraSmall",
  Widget: "small",
};
export const TEXT_FONT_SIZE_MAP = {
  Metrics: 14,
  SparkChart: 12,
  Widget: 12,
};

export type DimensionMap =
  | Record<L.Dimension, string>
  | Record<S.PerformanceDimension, string>
  | Record<CC.Dimension, string>
  // can replace above scp Dimension types
  | Record<DimensionColNames, string>;

export const DEFAULT_GET_DIMENSION_CELL = (
  dimensionData: DimensionMap,
  dimensionHeader: DimensionColumn
): CellData => {
  const value = dimensionData[dimensionHeader.dimensionVarName];
  return {
    label: value,
    value,
  };
};

export const toPrettyCPX: Formatter = (cpx: number, decimals = 0, zeroLabel = "--", min = 0.01) =>
  cpx && !isNaN(cpx) && cpx >= min
    ? cpx.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: !R.isNil(decimals) ? decimals : cpx >= 15 ? 0 : 2,
        maximumFractionDigits: !R.isNil(decimals) ? decimals : cpx >= 15 ? 0 : 2,
      })
    : zeroLabel;
export const toPrettySpend: Formatter = (
  spend: number,
  decimals = 0,
  zeroLabel = "--",
  min = 0.01
) =>
  spend && !isNaN(spend) && spend >= min
    ? spend.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: resolveDecimals(
          spend > 0 && spend < 1 && decimals === 0 ? 2 : decimals
        ),
        maximumFractionDigits: resolveDecimals(
          spend > 0 && spend < 1 && decimals === 0 ? 2 : decimals
        ),
      })
    : zeroLabel;
export const toPrettyNumber: Formatter = (num: number, decimals = 0, zeroLabel = "--", min = 0) =>
  num && !isNaN(num) && num >= min
    ? num.toLocaleString("en-US", {
        minimumFractionDigits: resolveDecimals(decimals),
        maximumFractionDigits: resolveDecimals(decimals),
      })
    : zeroLabel;
export const toPrettyPercent: Formatter = (num: number, decimals = 0, zeroLabel = "--", min = 0) =>
  num && !isNaN(num) && num >= min
    ? `${(num * 100.0).toLocaleString("en-US", {
        minimumFractionDigits: resolveDecimals(decimals),
        maximumFractionDigits: resolveDecimals(decimals),
      })}%`
    : zeroLabel;
export const toPretty1000sInteger: Formatter = (
  num: number,
  decimals = 0,
  zeroLabel = "--",
  min = 0
) =>
  num && !isNaN(num) && num / 1000.0 > 0 && num >= min
    ? ((num || 0) / 1000).toLocaleString("en-US", {
        minimumFractionDigits: resolveDecimals(decimals),
        maximumFractionDigits: resolveDecimals(decimals),
      })
    : zeroLabel;

export const FORMAT_MAP = {
  "000s": ((value, options) =>
    IMP_IN_THOUSANDS_FORMATTER(value, { ...options, min: -Infinity })) as FormatterNew,
  cpx: ((value, options) => CPX_FORMATTER(value, { ...options, min: -Infinity })) as FormatterNew,
  number: ((value, options) =>
    toPrettyNumberNew(value, { ...options, min: -Infinity })) as FormatterNew,
  percent: ((value, options) =>
    toPrettyPercentNew(value, {
      ...options,
      decimals: options?.decimals ?? 2,
      min: -Infinity,
    })) as FormatterNew,
  spend: ((value, options) =>
    SPEND_OR_REVENUE_FORMATTER(value, { ...options, min: -Infinity })) as FormatterNew,
};

export interface Formatter {
  (num: number, decimals?: number, zeroLabel?: string, min?: number, row?: PerformanceData): string;
}

type ALL_DIMS =
  | "Date"
  | CC.Dimension
  | L.Dimension
  | S.PerformanceDimension
  // for SCP refactor can replace other scp after we migrate
  | DimensionColNames
  | DimensionColPrettyNames
  | ColumnOrMetricName;

export interface ColumnMetaData<DIMS = ALL_DIMS> {
  aggregator?: (agg: Record<string, string | number>) => number | string; // We use this to calculate the total in the table
  category?: string; // Used for data columns
  contentType?: "date" | "logo" | "number" | "text" | "thumbnail"; // Used for dimensions
  customMetaData?: CustomMetric;
  decimals?: number;
  // Display-only hint for the preset editor's decimals placeholder when no explicit `decimals` is set
  // (e.g. percent columns whose formatter defaults to 2). Does not affect the value or rendering.
  defaultDecimals?: number;
  dimensionVarName?: DIMS;
  displayName: string; // Name displayed in the column header, can be overridden by the column header
  iconStyle?: "logo" | "thumbnail" | "iconOnly";
  isCustom?: boolean;
  fetchGetter?: (
    // We use this to calculate the value in the table if needed (done per cell)
    fetch: Record<string, string | number>,
    aggregatedData: Record<string, any>,
    customColFetchKey: (customColInfo: CustomMetric) => string,
    performanceRow: PerformanceDataRow
  ) => number | string;
  formatValue?: FormatterNew;
  minIsBest?: boolean;
  overlayText?: string;
  overlayPlacement?: Placement;
  requiredTotalsColumns?: (string | { dataVarName: string; fetchGetter: Function })[]; // If we need specific totals that get used in aggregator, we can specify them here
  currencyGetter?: (col: Record<string, string | number>) => string; // Gets currency code for relevant metrics
}

export type ColumnMetaDataMap =
  | Partial<Record<L.ColumnType, ColumnMetaData>>
  | Partial<Record<L.DimensionColumnType, ColumnMetaData>>
  | Partial<Record<S.ColumnType, ColumnMetaData>>
  | Partial<Record<S.PerformanceDimensionColumnType, ColumnMetaData>>
  | Partial<Record<CC.ColumnType, ColumnMetaData>>
  | Partial<Record<CC.DimensionColumnType, ColumnMetaData>>
  | Partial<Record<DimensionColNames, ColumnMetaData>>
  | Partial<Record<DimensionColPrettyNames, ColumnMetaData>>
  | Partial<Record<ColumnOrMetricName, ColumnMetaData>>;

/*
 * Helper for getting segments that appear in both selectedMap and groupedSegments
 */
export const useGroupedSegmentFiltering = (
  filterState: FilterPaneState,
  groupedSegments: Record<string, GroupedSegment[]>
): Record<string, string[]> => {
  return useMemo(() => {
    const result: Record<string, string[]> = {};

    if (!filterState.basic.selectedMap || !groupedSegments) {
      return result;
    }

    const { selectedMap } = filterState.basic;

    for (const [groupName, segments] of Object.entries(groupedSegments)) {
      const matchingSegments: string[] = [];

      for (const segment of segments) {
        const { segmentName } = segment;

        if (selectedMap[segmentName] && selectedMap[segmentName].Yes === true) {
          matchingSegments.push(segmentName);
        }
      }

      if (matchingSegments.length > 0) {
        result[groupName] = matchingSegments;
      }
    }

    return result;
  }, [filterState, groupedSegments]);
};

/*
 * Helpers for comparison metric data
 */
const getIsOtherData = (dataHeader): boolean =>
  dataHeader && dataHeader.dataVarName && dataHeader.dataVarName.includes("_other");
const getIsDelta = (dataHeader): boolean =>
  dataHeader && dataHeader.dataVarName && dataHeader.dataVarName.includes("_delta");
const getRootDataVarName = (rawName: string): string =>
  rawName.replace("_other", "").replace("_delta", "");
const getDelta = (value, otherValue) => {
  return !value || !otherValue || Math.abs(value) < 0.00005 || Math.abs(otherValue) < 0.00005
    ? "-"
    : Math.abs((value - otherValue) / otherValue) > 10000
    ? "-"
    : (value - otherValue) / otherValue;
};

/*
 * Helper for Custom Metrics
 */
const getDataFetchKeyForCustomMetrics = (
  dataHeader: Column,
  dataFetchKey: (dataHeader: Column) => string
) => {
  return customColInfo =>
    dataFetchKey({
      ...dataHeader,
      ...(customColInfo.kpi !== GLOBAL_KPI ? customColInfo : {}),
    });
};

export const constructTableData = (
  columnMetaDataMap: ColumnMetaDataMap,
  dataHeaders: Column[],
  dimensionHeaders: DimensionColumn[],
  getDimensionCell: (
    dimensionData: DimensionMap,
    dimensionHeader: DimensionColumn,
    ...args // Can pass in creative map, network map etc.
  ) => CellData,
  dataFetchKey: (dataHeader: Column) => string,
  performanceData: PerformanceData[],
  groupedSegments: Record<string, GroupedSegment[]> = {},
  shouldEnableMetricsTableSegmentGroups = false
): {
  aggregateData: CellData[];
  dimensionData: Record<string, CellData>[];
  tableData: CellData[][];
} => {
  // Filter out grouped segments from dimension headers and create grouped segments columns
  const groupedSegmentNames = new Set(
    Object.values(groupedSegments).flatMap(gs => gs.map(g => g.segmentName))
  );
  const groupedSegmentHeaders = dimensionHeaders.filter(
    header =>
      Object.keys(groupedSegments).some(groupName => header.dimensionTypeName === groupName) &&
      shouldEnableMetricsTableSegmentGroups
  );

  // Filter out if dimension is a grouped segment or a segment group
  const nonGroupedDimensionHeaders = shouldEnableMetricsTableSegmentGroups
    ? dimensionHeaders.filter(
        dimHeader =>
          !(
            groupedSegmentNames.has(dimHeader.dimensionTypeName) ||
            Object.keys(groupedSegments).some(
              groupName => dimHeader.dimensionTypeName === groupName
            )
          )
      )
    : dimensionHeaders;
  const aggregateData: CellData[] = []; // return type
  const aggregatedData: Record<string, any>[] = [];
  const dimensionData: Record<string, CellData>[] = [];
  const tableData: CellData[][] = [];
  // First go through the data and aggregate totals, we don't want to immediately
  // push to tableData because some columns might depend on totals.
  for (let columnIndex = 0; columnIndex < dataHeaders.length; ++columnIndex) {
    const dataHeader = dataHeaders[columnIndex];
    const columnMetaData =
      columnMetaDataMap &&
      dataHeaders &&
      dataHeader.dataVarName &&
      columnMetaDataMap[getRootDataVarName(dataHeader.dataVarName)]
        ? columnMetaDataMap[getRootDataVarName(dataHeader.dataVarName)]
        : undefined;

    const isOtherData = getIsOtherData(dataHeader);
    const isDelta = getIsDelta(dataHeader);
    const parsablecolumnMetaData = { ...columnMetaData };

    if (isOtherData && columnMetaData && columnMetaData.requiredTotalsColumns) {
      parsablecolumnMetaData.requiredTotalsColumns = columnMetaData.requiredTotalsColumns.map(
        col => `${col}_other`
      );
    }
    if (isDelta) {
      if (parsablecolumnMetaData.requiredTotalsColumns) {
        parsablecolumnMetaData.requiredTotalsColumns = parsablecolumnMetaData.requiredTotalsColumns.flatMap(
          col => [col, `${col}_other`]
        );
      } else {
        parsablecolumnMetaData.requiredTotalsColumns = [
          dataHeader.dataVarName.replace("_delta", ""),
          dataHeader.dataVarName.replace("_delta", "_other"),
        ];
      }
    }

    const { requiredTotalsColumns } = R.defaultTo(
      {
        aggregator: undefined,
      },
      parsablecolumnMetaData
    );
    const possibleCurrencyCodeAggCols = [
      DELIVERY_CURRENCY_CODE,
      `${DELIVERY_CURRENCY_CODE}_other`,
      PERFORMANCE_CURRENCY_CODE,
      `${PERFORMANCE_CURRENCY_CODE}_other`,
    ];
    const totalCurrCodeMap = {
      [DELIVERY_CURRENCY_CODE]: undefined,
      [`${DELIVERY_CURRENCY_CODE}_other`]: undefined,
      [PERFORMANCE_CURRENCY_CODE]: undefined,
      [`${PERFORMANCE_CURRENCY_CODE}_other`]: undefined,
    };
    for (let rowIndex = 0; rowIndex < performanceData.length; ++rowIndex) {
      const performanceRow = performanceData[rowIndex];
      const fetchKey = dataFetchKey(dataHeader);
      const dataFetchKeyForCustomMetrics = getDataFetchKeyForCustomMetrics(
        dataHeader,
        dataFetchKey
      );
      const fetch = R.defaultTo({}, performanceRow.fetches[fetchKey]);
      if (columnIndex === 0) {
        const dimensionRow: any = {};
        for (const dimensionHeader of nonGroupedDimensionHeaders) {
          const dimensionCell = getDimensionCell(
            performanceRow.dimensions as DimensionMap,
            dimensionHeader
          );
          dimensionRow[dimensionHeader.dimensionTypeName] = { ...dimensionCell };
        }

        for (const groupedSegmentHeader of groupedSegmentHeaders) {
          const segmentsInGroup = groupedSegments[groupedSegmentHeader.dimensionTypeName];
          const yesSegments: string[] = [];
          let hasNoValues = false;
          for (const segment of segmentsInGroup) {
            const rawValue = (performanceRow.dimensions as DimensionMap)[segment.segmentName];

            const tempDimensionHeader: DimensionColumn = {
              id: segment.segmentId?.toString() || "",
              dimensionTypeName: segment.segmentName,
              dimensionVarName: segment.segmentName,
              label: segment.segmentName,
              role: 10,
              divider: false,
            };

            const dimensionCell = getDimensionCell(
              performanceRow.dimensions as DimensionMap,
              tempDimensionHeader
            );
            const processedValue = dimensionCell?.label || dimensionCell?.value || rawValue;

            const isYes = processedValue && processedValue.toLowerCase() === "yes";
            hasNoValues = hasNoValues || (processedValue && processedValue.toLowerCase() === "no");

            if (isYes) {
              yesSegments.push(segment.segmentName);
            }
          }

          const sortedYesSegments = yesSegments.sort((a, b) => a.localeCompare(b));

          if (!R.isEmpty(sortedYesSegments)) {
            dimensionRow[groupedSegmentHeader.dimensionTypeName] = {
              label: sortedYesSegments.join(", "),
              value: sortedYesSegments.join(", "),
            };
          } else if (R.isEmpty(sortedYesSegments) && hasNoValues) {
            dimensionRow[groupedSegmentHeader.dimensionTypeName] = {
              label: "None",
              value: "None",
            };
          } else {
            dimensionRow[groupedSegmentHeader.dimensionTypeName] = {
              label: "Unlabeled",
              value: "Unlabeled",
            };
          }
        }

        dimensionData.push(dimensionRow);
      }
      const totals = {};
      if (requiredTotalsColumns) {
        for (const col of requiredTotalsColumns) {
          if (col === "numRows") {
            totals[col] = performanceData.length;
          } else if (typeof col === "string") {
            totals[col] = R.defaultTo(0, fetch[col]);
          } else {
            totals[col.dataVarName] = R.defaultTo(
              0,
              col.fetchGetter(fetch, aggregatedData, dataFetchKeyForCustomMetrics, performanceRow)
            );
          }
        }
      } else {
        totals[dataHeader.dataVarName] = R.defaultTo(0, fetch[dataHeader.dataVarName]);
      }
      if (columnMetaData && columnMetaData.customMetaData && columnMetaData.customMetaData.kpis) {
        for (const customColKPI of columnMetaData.customMetaData.kpis) {
          const kpiFetchKey = getDataFetchKeyForCustomMetrics(
            dataHeader,
            dataFetchKey
          )(customColKPI);
          totals[kpiFetchKey] = R.defaultTo({}, totals[kpiFetchKey]);
          totals[kpiFetchKey][customColKPI.dataVarName] =
            performanceRow &&
            performanceRow.fetches &&
            performanceRow.fetches[kpiFetchKey] &&
            performanceRow.fetches[kpiFetchKey][customColKPI.dataVarName]
              ? performanceRow.fetches[kpiFetchKey][customColKPI.dataVarName]
              : 0;
          totals[kpiFetchKey][`${customColKPI.dataVarName}_other`] =
            performanceRow &&
            performanceRow.fetches &&
            performanceRow.fetches[kpiFetchKey] &&
            performanceRow.fetches[kpiFetchKey][`${customColKPI.dataVarName}_other`]
              ? performanceRow.fetches[kpiFetchKey][`${customColKPI.dataVarName}_other`]
              : 0;
        }
      }
      if (rowIndex === 0) {
        aggregatedData.push(totals);
      } else {
        aggregatedData[columnIndex] = mergeNumberObjects(aggregatedData[columnIndex], totals);
        if (aggregatedData[columnIndex] && aggregatedData[columnIndex].numRows) {
          aggregatedData[columnIndex].numRows = performanceData.length;
        }
        if (aggregatedData[columnIndex]) {
          for (const currCodeCol of possibleCurrencyCodeAggCols) {
            if (aggregatedData[columnIndex][currCodeCol]) {
              const resolvedCurrCode =
                totals[currCodeCol] === "" || totals[currCodeCol] === 0
                  ? "USD"
                  : totals[currCodeCol];
              totalCurrCodeMap[currCodeCol] = totalCurrCodeMap[currCodeCol]
                ? resolvedCurrCode === totalCurrCodeMap[currCodeCol]
                  ? totalCurrCodeMap[currCodeCol]
                  : "mixed"
                : resolvedCurrCode;
              aggregatedData[columnIndex][currCodeCol] = totalCurrCodeMap[currCodeCol];
            }
          }
        }
      }
    }
  }
  // Now that we have totals, we can go through the data again and push to tableData
  // and bottomData
  for (let columnIndex = 0; columnIndex < dataHeaders.length; ++columnIndex) {
    const dataHeader = dataHeaders[columnIndex];
    const dataFetchKeyForCustomMetrics = getDataFetchKeyForCustomMetrics(dataHeader, dataFetchKey);
    const { ...columnMetaData } =
      columnMetaDataMap &&
      dataHeaders &&
      dataHeader &&
      dataHeader.dataVarName &&
      columnMetaDataMap[getRootDataVarName(dataHeader.dataVarName)]
        ? columnMetaDataMap[getRootDataVarName(dataHeader.dataVarName)]
        : // Unknown column (preset referencing a renamed/removed dataVarName) → empty meta (an
          // object, not undefined) so the later columnMetaData.formatValue assignment and the
          // downstream spread render empty instead of crashing the render loop.
          {};

    const isOtherData = getIsOtherData(dataHeader);
    const isDelta = getIsDelta(dataHeader);
    if (isDelta && columnMetaData) {
      // we want the metadata associated with the metric to use the agreegators ect but need a diff formatter
      columnMetaData.formatValue = toPrettyDeltaPercent;
    }

    const { aggregator, currencyGetter, fetchGetter, formatValue } = R.defaultTo(
      {
        aggregator: undefined,
        fetchGetter: undefined,
        formatValue: undefined,
        currencyGetter: undefined,
      },
      columnMetaData
    );
    const decimals = R.defaultTo(
      columnMetaData ? columnMetaData.decimals : undefined,
      dataHeader.decimals
    );
    const aggregatedCol = R.clone(R.defaultTo({}, aggregatedData[columnIndex]));

    if (isOtherData && (aggregator || currencyGetter)) {
      for (const colKey of Object.keys(aggregatedCol)) {
        if (typeof aggregatedCol[colKey] === "object") {
          for (const subColKey of Object.keys(aggregatedCol[colKey])) {
            if (subColKey.includes("_other")) {
              aggregatedCol[colKey][subColKey.replace("_other", "")] =
                aggregatedCol[colKey][subColKey];
            }
          }
        }
        if (colKey.includes("_other")) {
          aggregatedCol[colKey.replace("_other", "")] = aggregatedCol[colKey];
        }
      }
    }

    let value = aggregator
      ? aggregator(aggregatedCol, dataFetchKeyForCustomMetrics)
      : aggregatedCol[dataHeader.dataVarName];
    const currencyCode = currencyGetter ? currencyGetter(aggregatedCol) : undefined;

    if (isDelta) {
      if (!aggregator) {
        value = aggregatedCol[dataHeader.dataVarName.replace("_delta", "")];
        let val2 = aggregatedCol[dataHeader.dataVarName.replace("_delta", "_other")];
        value = getDelta(value, val2);
      } else {
        for (const colKey of Object.keys(aggregatedCol)) {
          if (typeof aggregatedCol[colKey] === "object") {
            for (const subColKey of Object.keys(aggregatedCol[colKey])) {
              if (subColKey.includes("_other")) {
                aggregatedCol[colKey][subColKey.replace("_other", "")] =
                  aggregatedCol[colKey][subColKey];
              }
            }
          }
          if (colKey.includes("_other")) {
            aggregatedCol[colKey.replace("_other", "")] = aggregatedCol[colKey];
          }
        }
        const val2 = aggregator(aggregatedCol, dataFetchKeyForCustomMetrics);
        value = getDelta(value, val2);
      }
    }

    const label = formatValue
      ? formatValue(value, { decimals: decimals, currCode: currencyCode })
      : `${typeof value === "number" && value === 0 ? "--" : value}`;
    const aggColData = {
      label,
      value,
    };
    aggregateData.push(aggColData);
    for (let rowIndex = 0; rowIndex < performanceData.length; ++rowIndex) {
      const performanceRow = performanceData[rowIndex];
      const fetchKey = dataFetchKey(dataHeader);
      const dataFetchKeyForCustomMetrics = getDataFetchKeyForCustomMetrics(
        dataHeader,
        dataFetchKey
      );
      const fetch: {} = R.defaultTo({}, performanceRow.fetches[fetchKey]);
      const gettablePerformanceRow = R.clone(performanceRow);
      if (isOtherData && (fetchGetter || currencyGetter)) {
        for (const fetchKey of R.keys(gettablePerformanceRow.fetches)) {
          for (const col of Object.keys(performanceRow.fetches[fetchKey])) {
            if (!col.includes("_other")) {
              gettablePerformanceRow.fetches[fetchKey][col] =
                performanceRow.fetches[fetchKey][`${col}_other`];
            }
          }
        }
        for (const col of Object.keys(aggregatedData[columnIndex])) {
          if (col.includes("_other")) {
            aggregatedData[columnIndex][col.replace("_other", "")] =
              aggregatedData[columnIndex][col];
          }
        }
      }
      const gettableFetch = R.defaultTo({}, gettablePerformanceRow.fetches[fetchKey]);
      const currCode = currencyGetter ? currencyGetter(gettableFetch) : undefined;
      let value = fetchGetter
        ? fetchGetter(
            gettableFetch,
            aggregatedData[columnIndex],
            dataFetchKeyForCustomMetrics,
            gettablePerformanceRow
          )
        : fetch[dataHeader.dataVarName];
      if (isDelta && fetchGetter) {
        for (const fetchKey of R.keys(gettablePerformanceRow.fetches)) {
          for (const col of Object.keys(performanceRow.fetches[fetchKey])) {
            if (!col.includes("_other")) {
              gettablePerformanceRow.fetches[fetchKey][col] =
                performanceRow.fetches[fetchKey][`${col}_other`];
            }
          }
        }
        const gettableFetch = R.defaultTo({}, gettablePerformanceRow.fetches[fetchKey]);
        const val2 = fetchGetter
          ? fetchGetter(
              gettableFetch,
              aggregatedData[columnIndex],
              dataFetchKeyForCustomMetrics,
              gettablePerformanceRow
            )
          : fetch[dataHeader.dataVarName];
        value = getDelta(value, val2);
      }
      const label =
        columnMetaData && columnMetaData.formatValue
          ? columnMetaData.formatValue(value, {
              decimals: decimals,
              row: performanceRow,
              currCode: currCode,
            })
          : `${value}`;
      if (columnIndex === 0) {
        tableData.push([
          {
            label,
            value,
          },
        ]);
      } else {
        tableData[rowIndex].push({
          label,
          value,
        });
      }
    }
  }
  return {
    aggregateData,
    dimensionData,
    tableData,
  };
};

export const sortData = (
  dataHeaders: Column[],
  dimensionData: Record<string, CellData>[],
  dimensionDataHeaders: DimensionColumn[],
  sorting: SortInfo[],
  tableData: CellData[][],
  hasDimensionData: boolean
): [Record<string, CellData>[], CellData[][]] => {
  if (R.isNil(sorting) || R.isEmpty(sorting)) {
    return [dimensionData, tableData];
  }
  const combinedData = R.zip(
    hasDimensionData ? dimensionData : [...tableData].map(() => ({})),
    R.map(
      row => R.map(cell => ({ ...cell, value: cell.value === "--" ? 0 : cell.value }), row),
      tableData
    )
  );
  const reducedSorting = sorting.reduce((sorts, { id, asc }) => {
    const sorter = (() => {
      const func = asc ? R.ascend : R.descend;
      const dimensionCol = R.find(col => col.id === id, dimensionDataHeaders);
      if (dimensionCol) {
        const getter = row => {
          return row[0][dimensionCol.dimensionTypeName].value;
        };
        return func(getter);
      }
      const i = R.findIndex(col => col.id === id, dataHeaders) || 0;
      const column = dataHeaders[i];
      if (!column) {
        return undefined;
      }
      return func((row: any) => {
        return row[1][i].value;
      });
    })();
    if (sorter) {
      return [...sorts, sorter];
    }
    return sorts;
  }, [] as ((a: any, b: any) => number)[]);
  const sortedCombinedData = R.sortWith(reducedSorting, combinedData);
  const sortedDimensionData = R.map(elem => elem[0], sortedCombinedData);
  const sortedTableData = R.map(elem => elem[1], sortedCombinedData);
  return [sortedDimensionData, sortedTableData];
};
export const getHeatMap = (
  dataHeaders: Column[],
  tableData: CellData[][]
): (HeatMapData | null)[] => {
  const heatMap: (HeatMapData | null)[] = [];
  for (let colIndex = 0; colIndex < dataHeaders.length; ++colIndex) {
    const column = dataHeaders[colIndex];
    if (column.heatMapping) {
      let max = -Infinity;
      let min = Infinity;
      let sum = 0;
      const medianNumbers: number[] = [];
      for (let row of tableData) {
        const val = row[colIndex].value;
        if (typeof val !== "number" || [Infinity, -Infinity].includes(val) || isNaN(val)) {
          continue;
        }
        if (val < min) {
          min = val;
        }
        if (val > max) {
          max = val;
        }
        sum += val;
        medianNumbers.push(val);
      }
      // If the config minimum is bigger than the actual minimum, use that one
      if (!R.isNil(column.heatMapping.min) && column.heatMapping.min > min) {
        ({ min } = column.heatMapping);
      }
      // If the config maximum is smaller than the actual maximum, use that one
      if (!R.isNil(column.heatMapping.max) && column.heatMapping.max < max) {
        ({ max } = column.heatMapping);
      }
      const mean = sum / medianNumbers.length;
      const stdClip = R.isNil(column.heatMapping.stdevClipping)
        ? 1
        : column.heatMapping.stdevClipping;
      if (stdClip > 0) {
        let stdevCalc = 0;
        for (let item of tableData) {
          if (
            typeof item[colIndex].value === "number" &&
            (!column.heatMapping.excludeZeros || item[colIndex].value !== 0) &&
            ![Infinity, -Infinity].includes(item[colIndex].value as number) &&
            !isNaN(item[colIndex].value as number)
          ) {
            stdevCalc += ((item[colIndex].value as number) - mean) ** 2;
          }
        }
        const stdev = Math.sqrt(stdevCalc / medianNumbers.length);
        min = Math.max(min, mean - stdev * stdClip);
        max = Math.min(max, mean + stdev * stdClip);
      }
      let midpoint: number;
      switch (column.heatMapping.midpoint) {
        case "MEDIAN":
          midpoint = medianNumbers.length
            ? medianNumbers.sort()[Math.floor(medianNumbers.length / 2)]
            : 0;
          break;
        case "MEAN":
          midpoint = mean;
          break;
        case "CUSTOM":
          midpoint = column.heatMapping?.customMidpoint || 0;
          break;
        default:
          midpoint = (max - min) / 2 + min;
          break;
      }
      heatMap.push({
        max,
        midpoint,
        min,
      });
    } else {
      heatMap.push(null);
    }
  }
  return heatMap;
};

export interface CellDataHeatMapInfo {
  backgroundColor: string;
  outline: string;
}

export const getHeatMapInfo = (
  columnIndex: number,
  columnMetaDataMap: ColumnMetaDataMap,
  dataHeaders: Column[],
  heatMap: (HeatMapData | null)[],
  value: number
): CellDataHeatMapInfo => {
  const heatMapInfo = heatMap[columnIndex];
  const col = dataHeaders[columnIndex] as Column;
  const colHeatMapMetaData = R.defaultTo(
    {
      colorScheme: "sequential",
      excludeZeros: false,
      minIsBest: undefined,
    },
    col.heatMapping
  );
  const colMetaDataMap = R.defaultTo(
    { minIsBest: undefined, contentReplacement: undefined },
    columnMetaDataMap[dataHeaders[columnIndex].dataVarName]
  );
  if (!(heatMap && colHeatMapMetaData && heatMapInfo)) {
    return { backgroundColor: "transparent", outline: "none" };
  }
  const { min, max, midpoint } = heatMapInfo as HeatMapData;
  const { colorScheme, excludeZeros, minIsBest: presetMinIsBest } = colHeatMapMetaData;
  const { minIsBest: columnMinIsBest, contentReplacement } = colMetaDataMap;
  const minIsBest = R.defaultTo(columnMinIsBest, presetMinIsBest);
  if (
    min === max ||
    (excludeZeros && value === 0) ||
    (contentReplacement && value <= contentReplacement.threshold)
  ) {
    return { backgroundColor: "transparent", outline: "none" };
  }
  if (colorScheme && colorScheme === "diverging") {
    const backgroundColor = getSteppedHeatColor(
      {
        min,
        max,
        midpoint,
        stops: minIsBest ? DIVERGENT_STOPS_MIN_IS_BEST : DivergentColorMap20,
      },
      value
    );
    const arm = value > midpoint ? max - midpoint : midpoint - min;
    const proximity = arm > 0 ? Math.min(1, Math.abs(value - midpoint) / arm) : 1;
    return {
      backgroundColor,
      outline: proximity < 0.05 ? `1px solid ${DivergentGreenDark}` : "none",
    };
  } else if (colorScheme && colorScheme === "sequential") {
    // No `midpoint`: a magnitude scale spans the domain in a single arm.
    return {
      backgroundColor: getSteppedHeatColor(
        { min, max, stops: minIsBest ? SEQUENTIAL_STOPS_MIN_IS_BEST : SequentialGreenRamp },
        value
      ),
      outline: "none",
    };
  }
  return {
    backgroundColor: "none",
    outline: "none",
  };
};

export const adjustSupersAfterRemoval = (headers: any[], i: number): any[] =>
  headers.reduce((headers, header) => {
    // If it's a one-column-wide header and that column is the one we're deleting, delete
    // the header
    if (header.start === header.end && header.start === i) {
      return headers;
    }
    // If the header contains the element, then the end position shifts down, but the
    // start position remains the same.
    if (header.start <= i && header.end >= i) {
      return [...headers, { ...header, end: header.end - 1 }];
    }
    // If the header is before this column, add it in and continue
    if (header.end < i) {
      return [...headers, header];
    }
    // At this point we know the header doesn't contain the column and isn't before the
    // column. Thus, it must be after. That means the start and end need to be shifted
    // down because a column before it was removed.
    return [...headers, { ...header, start: header.start - 1, end: header.end - 1 }];
  }, [] as any[]);

export const useSortAdder = (
  setSorting: StateSetter<SortInfo[]>,
  disableMulti = false
): ((id: string) => void) =>
  useCallback(
    (id: string) => {
      //  If a user selects  an already-sorted column, it flips it if it's descending, and removes it otherwise.
      setSorting(sorting => {
        let newSorting: SortInfo[] = [];
        let sawSelf = false;
        for (const sortItem of sorting) {
          if (sortItem.id === id) {
            sawSelf = true;
            // If it's descending, then flip. Otherwise, skip.
            if (!sortItem.asc) {
              newSorting.push({ id, asc: true });
            }
          } else {
            newSorting.push(sortItem);
          }
        }
        if (!sawSelf) {
          if (disableMulti) {
            // If we pick an entirely new column to sort, we want to clear out
            // all the current sorting.
            newSorting = [{ id, asc: false }];
          } else {
            newSorting.push({ id, asc: false });
          }
        }
        return newSorting;
      });
    },
    [disableMulti, setSorting]
  );

export const areFilterStatesEqual = (
  state1: FilterPaneState | null | undefined,
  state2: FilterPaneState | null | undefined
): boolean => {
  if (R.isNil(state1) || R.isNil(state2)) {
    // If one of the state is undefined, we can check that they are both undefined or not
    return state1 === state2;
  }
  if (state1.isAdvanced !== state2.isAdvanced) {
    return false;
  } else if (state1.isAdvanced) {
    return state1.advanced === state2.advanced;
  } else {
    const notMap1 = state1.basic.notMap;
    const notMap2 = state2.basic.notMap;
    if (R.keys(notMap1).length !== R.keys(notMap2).length) {
      return false;
    } else {
      const notMap1Keys = R.keys(notMap1).sort();
      const notMap2Keys = R.keys(notMap2).sort();
      for (let i = 0; i < notMap1Keys.length; i++) {
        const key1 = notMap1Keys[i];
        const key2 = notMap2Keys[i];
        if (key1 !== key2 || notMap1[key1] !== notMap2[key2]) {
          return false;
        }
      }
      const selectedMap1 = state1.basic.selectedMap;
      const selectedMap2 = state2.basic.selectedMap;
      if (R.keys(selectedMap1).length !== R.keys(selectedMap2).length) {
        return false;
      } else {
        const selectedMap1Keys = R.keys(selectedMap1).sort();
        const selectedMap2Keys = R.keys(selectedMap2).sort();
        for (let i = 0; i < selectedMap1Keys.length; i++) {
          const key1 = selectedMap1Keys[i];
          const key2 = selectedMap2Keys[i];
          if (key1 !== key2) {
            return false;
          }
          const dimensionMap1 = selectedMap1[key1];
          const dimensionMap2 = selectedMap2[key2];
          if (R.keys(dimensionMap1).length !== R.keys(dimensionMap2).length) {
            return false;
          } else {
            const dimensionMap1Keys = R.keys(dimensionMap1).sort();
            const dimensionMap2Keys = R.keys(dimensionMap2).sort();
            for (let i = 0; i < dimensionMap1Keys.length; i++) {
              const key1 = dimensionMap1Keys[i];
              const key2 = dimensionMap2Keys[i];
              if (key1 !== key2 || dimensionMap1[key1] !== dimensionMap2[key2]) {
                return false;
              }
            }
          }
        }
      }
    }
    return true;
  }
};

export const getDataHeaders = (preset: Partial<MetricsTablePreset>, role: number): Column[] => {
  const dataColumns = preset && preset.dataColumns ? preset.dataColumns : [];
  return dataColumns.filter(col => (col && col.role ? col.role >= role : true));
};

export const getDimensionDataHeaders = (
  preset: Partial<MetricsTablePreset>,
  role: number,
  groupedSegments: Record<string, GroupedSegment[]> = {},
  shouldEnableMetricsTableSegmentGroups = false
): DimensionColumn[] => {
  const dimensionColumns = preset && preset.dimensionColumns ? preset.dimensionColumns : [];
  const filteredColumns = dimensionColumns.filter(col =>
    col && col.role ? col.role >= role : true
  );

  if (!shouldEnableMetricsTableSegmentGroups) {
    return filteredColumns;
  }

  const groupedSegmentNames = Object.values(groupedSegments).flatMap(gs =>
    gs.map(g => g.segmentName)
  );
  const nonGroupedColumns = filteredColumns.filter(
    col => !groupedSegmentNames.some(bs => bs === col.dimensionTypeName)
  );

  nonGroupedColumns.map(col => {
    if (
      Object.keys(groupedSegments).some(groupName => col.dimensionTypeName === groupName) &&
      shouldEnableMetricsTableSegmentGroups
    ) {
      return {
        id: `grouped-segments-${col.dimensionTypeName.toLowerCase().replace(" ", "-")}`,
        dimensionTypeName: col.dimensionTypeName,
        dimensionVarName: col.dimensionVarName,
        label: col.dimensionVarName,
        role: 10,
        divider: false,
      };
    } else {
      return col;
    }
  });

  return nonGroupedColumns;
};

const COMPARISON_SUB_HEADER_OPTIONS = new Set(["primary", "comparison", "%∆"]);

export const getExportedData = ({
  columnMetaDataMap,
  dataHeaders,
  dimensionDataHeaders,
  numDataCols,
  numDimCols,
  sortedDimensionData,
  sortedTableData,
}: {
  columnMetaDataMap: ColumnMetaDataMap;
  dataHeaders: Column[];
  dimensionDataHeaders: DimensionColumn[];
  numDataCols: number;
  numDimCols: number;
  sortedDimensionData: Record<string, CellData>[];
  sortedTableData: CellData[][];
}): Array<Array<string | number>> => {
  const exportedData: (string | number)[][] = [];
  for (let rowIndex = 0; rowIndex <= sortedDimensionData.length; rowIndex++) {
    const row: (string | number)[] = [];
    for (let leftColIndex = 0; leftColIndex < numDimCols; leftColIndex++) {
      if (rowIndex === 0) {
        const dimensionTypeName = R.pathOr(
          "",
          [leftColIndex, "dimensionTypeName"],
          dimensionDataHeaders
        );
        row.push(
          R.defaultTo(
            R.pathOr(dimensionTypeName, [dimensionTypeName, "displayName"], columnMetaDataMap),
            R.pathOr(null, [leftColIndex, "label"], dimensionDataHeaders)
          ) as string
        );
      } else {
        row.push(
          sortedDimensionData[rowIndex - 1][dimensionDataHeaders[leftColIndex].dimensionTypeName]
            .value
        );
      }
    }
    for (let colIndex = 0; colIndex < numDataCols; colIndex++) {
      if (rowIndex === 0) {
        const dataHeaderLabel = dataHeaders[colIndex].label;
        const colDisplayName =
          columnMetaDataMap[getRootDataVarName(dataHeaders[colIndex].dataVarName)]?.displayName;
        if (
          dataHeaderLabel &&
          colDisplayName &&
          COMPARISON_SUB_HEADER_OPTIONS.has(dataHeaderLabel)
        ) {
          row.push(`${colDisplayName} ${dataHeaderLabel}`);
        } else {
          row.push(R.defaultTo(colDisplayName, dataHeaderLabel));
        }
      } else {
        row.push(sortedTableData[rowIndex - 1][colIndex].value);
      }
    }
    exportedData.push(row);
  }

  return exportedData;
};

export const transformEmailToFullName = (email: string): string => {
  if (R.isNil(email) || !email.includes("@")) {
    return R.defaultTo("", email);
  }
  const username = email.split("@")[0];
  const charArray = username.split("");
  for (let i = 0; i < charArray.length; i++) {
    if (i === 0) {
      charArray[i] = charArray[i].toUpperCase();
    } else if (charArray[i - 1] === ".") {
      charArray[i - 1] = " ";
      charArray[i] = charArray[i].toUpperCase();
    }
  }
  return charArray.join("");
};

export const getTextWidth = (
  text: string,
  fontSize = 14, // Defaults here are for cross/single channel page dimension cells
  fontWeight = 600,
  font = "Geist",
  // Create a canvas element dynamically if not passed in
  canvas = document.createElement("canvas")
): number => {
  const context = canvas.getContext("2d");
  // Set the font to match your fixed font size and style
  if (context) {
    context.font = `${fontWeight} ${fontSize}px ${font}`;
    // Measure the text
    const metrics = context.measureText(text);
    // Return the width of the text
    return metrics.width;
  }
  return -1;
};

// Update types to add these to the column metadata map
export const convertDBCustomMetric = (metric: CustomMetric): ColumnMetaData | undefined => {
  try {
    const aggregator = ((agg, dataFetchKey) => {
      try {
        let functionString = metric.function_string;
        for (const formulaKPI of metric.kpis) {
          const kpiFetchKey = dataFetchKey(formulaKPI);
          functionString = functionString.replace(
            `fetch.${formulaKPI.dataVarName}`,
            `agg["${kpiFetchKey}"]["${formulaKPI.dataVarName}"]`
          );
        }
        return evaluate(
          `${functionString.replace(/fetch./g, "agg.")}${
            metric.required_columns.includes("numRows") ? "/agg.numRows" : ""
          }`,
          { agg }
        );
      } catch (e) {
        return 0;
      }
    }) as (agg: Record<string, string | number>) => string | number;
    const fetchGetter = ((fetch, aggregatedData, dataFetchKey, performanceRow) => {
      try {
        let functionString = metric.function_string;
        const kpiData: Record<string, Record<string, number>> = {};
        for (const formulaKPI of metric.kpis) {
          const kpiFetchKey = dataFetchKey(formulaKPI);
          const kpiFetch = R.defaultTo({}, performanceRow.fetches[kpiFetchKey]);
          kpiData[kpiFetchKey] = R.defaultTo({}, kpiData[kpiFetchKey]);
          kpiData[kpiFetchKey][formulaKPI.dataVarName] = R.defaultTo(
            0,
            kpiFetch[formulaKPI.dataVarName]
          );
          functionString = functionString.replace(
            `fetch.${formulaKPI.dataVarName}`,
            `kpiData["${kpiFetchKey}"]["${formulaKPI.dataVarName}"]`
          );
        }
        return evaluate(functionString, { fetch, aggregatedData, kpiData });
      } catch (e) {
        return 0;
      }
    }) as (
      // We use this to calculate the value in the table if needed (done per cell)
      fetch: Record<string, string | number>,
      aggregatedData: Record<string, any>
    ) => number | string;
    return {
      aggregator,
      contentType: "number",
      customMetaData: metric,
      decimals: metric.decimals,
      displayName: metric.display_name,
      fetchGetter,
      formatValue: R.isNil(metric.formatter) ? FORMAT_MAP.number : FORMAT_MAP[metric.formatter],
      isCustom: true,
      minIsBest: metric.min_is_best,
      requiredTotalsColumns: metric.required_columns,
    };
  } catch (e) {
    console.error(`Error: ${e}`);
  }
};

export const joinMetricsTableData = (
  data: PerformanceDataRow[],
  otherData: PerformanceDataRow[],
  start: string | undefined,
  end: string | undefined,
  otherStart: string | undefined,
  otherEnd: string | undefined,
  tablePreset: MetricsTablePreset | undefined
): PerformanceDataRow[] => {
  const result = [] as PerformanceDataRow[];
  let dateGrouping = "day";
  if (tablePreset && tablePreset.dimensionColumns) {
    for (const dimCol of tablePreset.dimensionColumns) {
      if (dimCol.dateGrouping) {
        ({ dateGrouping } = dimCol);
        break;
      }
    }
  }
  const toDate = (d: string) => {
    const cleanDate = d.split("T")[0];
    return new Date(`${cleanDate}T00:00:00`);
  };
  const sortedData = R.sort(
    R.ascend(row => new Date(`${row.dimensions.Date}`)),
    R.clone(data)
  ) as PerformanceDataRow<string, Partial<Record<string, number>>>[];
  const sortedOtherData = R.sort(
    R.ascend(row => new Date(`${row.dimensions.Date}`)),
    R.clone(otherData)
  ) as PerformanceDataRow<string, Partial<Record<string, number>>>[];
  const startDate = start ? toDate(start) : null;
  const otherStartDate = otherStart ? toDate(otherStart) : null;
  const workingData = R.map(row => {
    if (
      R.isNil(row.dimensions.Date) ||
      !startDate ||
      !otherStartDate ||
      R.isNil(end) ||
      R.isNil(otherEnd)
    ) {
      return row;
    }
    const rowDate = toDate(`${row.dimensions.Date}`);
    let targetDate: Date;
    if (dateGrouping === "month") {
      const monthDiff = Dfns.differenceInCalendarMonths(otherStartDate)(startDate);
      targetDate = Dfns.subMonths(monthDiff)(rowDate);
    } else if (dateGrouping === "week") {
      const weekDiff = Dfns.differenceInCalendarWeeks(otherStartDate)(startDate);
      targetDate = Dfns.subWeeks(weekDiff)(rowDate);
    } else if (dateGrouping === "quarter") {
      const quarterDiff = Dfns.differenceInCalendarQuarters(otherStartDate)(startDate);
      targetDate = Dfns.subQuarters(quarterDiff)(rowDate);
    } else if (dateGrouping === "year") {
      const yearDiff = Dfns.differenceInCalendarYears(otherStartDate)(startDate);
      targetDate = Dfns.subYears(yearDiff)(rowDate);
    } else {
      const dayDiff = Dfns.differenceInCalendarDays(otherStartDate)(startDate);
      targetDate = Dfns.subDays(dayDiff)(rowDate);
    }
    return {
      ...row,
      dimensions: {
        ...row.dimensions,
        ComparisonDate: Dfns.format(DATE_FORMAT)(targetDate),
      },
    };
  }, sortedData) as PerformanceDataRow<string, Partial<Record<string, number>>>[];
  const otherWorkingData: (PerformanceDataRow & { addedToJoinedData?: boolean })[] = R.map(row => {
    if (
      R.isNil(row.dimensions.Date) ||
      !startDate ||
      !otherStartDate ||
      R.isNil(end) ||
      R.isNil(otherEnd)
    ) {
      return row;
    }
    const rowDate = toDate(`${row.dimensions.Date}`);
    let targetDate: Date;
    if (dateGrouping === "month") {
      const monthDiff = Dfns.differenceInCalendarMonths(otherStartDate)(startDate);
      targetDate = Dfns.addMonths(monthDiff)(rowDate);
    } else if (dateGrouping === "week") {
      const weekDiff = Dfns.differenceInCalendarWeeks(otherStartDate)(startDate);
      targetDate = Dfns.addWeeks(weekDiff)(rowDate);
    } else if (dateGrouping === "quarter") {
      const quarterDiff = Dfns.differenceInCalendarQuarters(otherStartDate)(startDate);
      targetDate = Dfns.addQuarters(quarterDiff)(rowDate);
    } else if (dateGrouping === "year") {
      const yearDiff = Dfns.differenceInCalendarYears(otherStartDate)(startDate);
      targetDate = Dfns.addYears(yearDiff)(rowDate);
    } else {
      const dayDiff = Dfns.differenceInCalendarDays(otherStartDate)(startDate);
      targetDate = Dfns.addDays(dayDiff)(rowDate);
    }
    return {
      ...row,
      dimensions: {
        ...row.dimensions,
        ComparisonDate: row.dimensions.Date,
        Date: Dfns.format(DATE_FORMAT)(targetDate),
      },
    };
  }, sortedOtherData) as (PerformanceDataRow & { addedToJoinedData?: boolean })[];
  for (const item of workingData) {
    const currDims = item.dimensions;
    const newItem = { dimensions: { ...currDims }, fetches: {} };
    const matchingOtherData = otherWorkingData.find(item => {
      const otherDims = item.dimensions;
      for (const dimKey of R.keys(currDims)) {
        if (!(dimKey in otherDims) || currDims[dimKey] !== otherDims[dimKey]) {
          return false;
        }
      }
      item.addedToJoinedData = true;
      return true;
    });
    for (const fetchKey of R.keys(item.fetches)) {
      newItem.fetches[fetchKey] = { ...item.fetches[fetchKey] };
      for (const col of R.keys(item.fetches[fetchKey])) {
        newItem.fetches[fetchKey][`${col as string}_other`] =
          matchingOtherData && matchingOtherData.fetches[fetchKey]
            ? matchingOtherData.fetches[fetchKey][col]
            : null;
        const val = newItem.fetches[fetchKey][col] || 0;
        const otherVal = newItem.fetches[fetchKey][`${col as string}_other`] || 0;
        newItem.fetches[fetchKey][`${col as string}_delta`] = getDelta(val, otherVal);
      }
    }
    result.push(newItem);
  }
  const remainingOtherData = otherWorkingData.filter(item => !item.addedToJoinedData);
  for (const item of remainingOtherData) {
    const currDims = item.dimensions;
    const newItem = { dimensions: { ...currDims }, fetches: {} };
    for (const fetchKey of R.keys(item.fetches)) {
      newItem.fetches[fetchKey] = { ...item.fetches[fetchKey] };
      for (const col of R.keys(item.fetches[fetchKey])) {
        newItem.fetches[fetchKey][`${col as string}_other`] = newItem.fetches[fetchKey][col];
        newItem.fetches[fetchKey][col] = null;
        newItem.fetches[fetchKey][`${col as string}_delta`] = getDelta(null, null);
      }
    }
    result.push(newItem);
  }
  return result;
};

export const convertKPIMapToOptions = (
  prefix: string,
  kpiMap: Record<string, KpiInfo> = {}
): { label: string; value: string; trailingContent?: JSX.Element }[] => {
  const options: { label: string; value: string; trailingContent?: JSX.Element }[] = [];
  if (R.isNil(kpiMap)) {
    return options;
  }
  for (const kpi of R.keys(kpiMap)) {
    const kpiInfo = kpiMap[kpi];
    const option: { label: string; value: string; trailingContent?: JSX.Element } = {
      label: kpiInfo.name,
      value: kpiInfo.name,
    };
    const uniqKeys = R.filter(
      key => !R.isNil(kpiInfo[key]) && (kpiInfo[key] as string).length > 0,
      R.keys(R.omit(["id", "name"], kpiInfo))
    );
    if (uniqKeys.length) {
      option.label = `${kpiInfo.name} (${R.map(key => kpiInfo[key], uniqKeys).join(", ")})`;
      option.value = kpi;
    } else if (prefix === COMMERCE) {
      option.label = kpiInfo.name;
      option.value = kpi;
    }
    options.push(option);
  }
  return R.sortBy(R.prop("label"), options);
};

export const createComparisonTablePreset = (preset: MetricsTablePreset): MetricsTablePreset => {
  const compPreset = R.clone(preset);
  const newSuperHeaders: SuperHeader[] = compPreset.dataColumns
    ? compPreset.dataColumns.map(col => {
        return {
          data: {
            text: col.label ? col.label : col.dataVarName,
            leftDivider: true,
            rightDivider: true,
          },
          span: 3,
        };
      })
    : [];
  const newDataCols = compPreset.dataColumns
    ? compPreset.dataColumns.reduce((newCols, col) => {
        newCols.push(
          { ...col, label: "primary" },
          { ...col, id: uuid.v4(), dataVarName: `${col.dataVarName}_other`, label: "comparison" },
          {
            ...col,
            id: uuid.v4(),
            dataVarName: `${col.dataVarName}_delta`,
            label: "%∆",
            decimals: 0,
          }
        );
        return newCols;
      }, [] as Column[])
    : [];
  const newDimCols = compPreset.dimensionColumns
    ? compPreset.dimensionColumns.reduce((newCols, col) => {
        newCols.push(col);
        if (col.dimensionVarName === "Date") {
          newCols.push({
            ...col,
            dimensionTypeName: `Comparison${col.dimensionTypeName}`,
            dimensionVarName: `Comparison${col.dimensionVarName}`,
            id: uuid.v4(),
            label: "Comparison Date",
          });
        }
        return newCols;
      }, [] as DimensionColumn[])
    : [];
  compPreset.dataColumns = newDataCols;
  compPreset.dimensionColumns = newDimCols;
  compPreset.superHeaders = newSuperHeaders;
  return compPreset;
};
